import React, { useEffect, useRef, useState } from "react";

const PALETTE = [
    { name: "sage", value: "#B7CDB1" },
    { name: "amber", value: "#E7C36F" },
    { name: "plum", value: "#C7A0B0" },
    { name: "slate", value: "#9CB0C0" },
    { name: "clay", value: "#D8B49E" },
];

/**
 * Renders a floating swatch strip anchored near a long-press point.
 * If the pointer is released over a swatch, calls `onPick(color)`.
 * If the pointer is released anywhere else, calls `onDismiss()`.
 *
 * The parent (Reader/ReaderPage) is expected to keep this mounted only
 * while a long-press has just fired and the pointer is still down.
 */
export default function QuickHighlightStrip({ anchor, onPick, onDismiss }) {
    const stripRef = useRef(null);
    const [hover, setHover] = useState(null);

    // Position: centered horizontally on anchor.x, above anchor.y by ~56px,
    // clamped to viewport with a small margin.
    const [pos, setPos] = useState({ left: 0, top: 0 });
    useEffect(() => {
        const el = stripRef.current;
        if (!el || !anchor) return;
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        const margin = 8;
        let left = Math.round(anchor.x - width / 2);
        let top = Math.round(anchor.y - height - 14);
        // Flip below if not enough room above
        if (top < margin) top = Math.round(anchor.y + 20);
        left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
        setPos({ left, top });
    }, [anchor]);

    // Track pointer while gesture is live
    useEffect(() => {
        const handleMove = (e) => {
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const swatch = el?.closest?.("[data-quick-swatch]");
            const color = swatch?.getAttribute("data-quick-swatch") || null;
            setHover(color);
        };
        const handleUp = (e) => {
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const swatch = el?.closest?.("[data-quick-swatch]");
            const color = swatch?.getAttribute("data-quick-swatch") || null;
            if (color) onPick?.(color);
            else onDismiss?.();
        };
        const handleCancel = () => onDismiss?.();
        window.addEventListener("pointermove", handleMove, { passive: true });
        window.addEventListener("pointerup", handleUp, { passive: true });
        window.addEventListener("pointercancel", handleCancel, { passive: true });
        return () => {
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", handleUp);
            window.removeEventListener("pointercancel", handleCancel);
        };
    }, [onPick, onDismiss]);

    return (
        <div
            ref={stripRef}
            data-testid="quick-highlight-strip"
            className="fixed z-50 pointer-events-none"
            style={{ left: pos.left, top: pos.top }}
        >
            <div
                className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 backdrop-blur-md shadow-xl border border-ink-10 animate-fade-in"
            >
                {PALETTE.map(c => {
                    const active = hover === c.name;
                    return (
                        <span
                            key={c.name}
                            data-quick-swatch={c.name}
                            data-testid={`quick-swatch-${c.name}`}
                            className={
                                "block rounded-full ring-1 transition-all duration-100 " +
                                (active ? "w-11 h-11 ring-ink" : "w-8 h-8 ring-ink-10")
                            }
                            style={{ background: c.value }}
                            aria-label={`Highlight ${c.name}`}
                        />
                    );
                })}
            </div>
            <div className="mt-1 text-center font-ui text-[10px] uppercase tracking-[0.18em] text-ink-40 select-none">
                {hover ? `Release to highlight — ${hover}` : "Slide to a colour"}
            </div>
        </div>
    );
}
